import { useCallback, useEffect, useMemo, useState, type RefObject } from 'react';
import Map, { Layer, Marker, Popup, Source, type MapRef } from 'react-map-gl/maplibre';
import { METRO_BBOX, bboxOfPolygon } from '@soc/shared';
import { useGetTowersQuery } from '../../app/api';
import { useActiveAlerts, useSelectedAlert } from '../alerts/selectors';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useSelectedProject } from '../projects/useSelectedProject';
import { Pin } from './Pin';
import { ClusterPin } from './ClusterPin';
import { AlertPopupBody } from './AlertPopupBody';
import { ClusterPopupBody } from './ClusterPopupBody';
import { useClusters } from './useClusters';
import { choosePopupAnchor } from './popupAnchor';
import { clearHighlight, clearSelection, selectAlert, selectCluster } from './mapUiSlice';
import { InvestigationLayer } from './InvestigationLayer';
import { useInvestigateMutation } from '../../app/api';

// Rough rendered footprints (see AlertPopupBody / ClusterPopupBody) — used only
// to decide which way a popup should open before it would clip the map edge.
const ALERT_POPUP_SIZE = { width: 280, height: 200 };
const CLUSTER_POPUP_SIZE = { width: 300, height: 340 };

// Free CARTO dark vector style — no API key required.
const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

// Stable per-pin sonar-ring phase derived from the alert id (not the array
// index) so reordering the alert list — a new alert arriving, a rule toggling —
// never reassigns delays and restarts every pin's animation.
function pinDelay(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return (Math.abs(h) % 6) * 0.45;
}

export function CellMap({ mapRef }: { mapRef: RefObject<MapRef> }) {
  const dispatch = useAppDispatch();
  const project = useSelectedProject();
  // Towers are scoped to the active project's polygon.
  const { data: towers } = useGetTowersQuery({ polygon: project?.polygon });
  const alerts = useActiveAlerts();

  const selection = useAppSelector((s) => s.mapUi.selection);
  const highlightedAlertId = useAppSelector((s) => s.mapUi.highlightedAlertId);
  const selectedAlert = useSelectedAlert();

  // Investigate Mode: when active, hide the normal pins/towers and draw the
  // evidence layer instead. The result comes from the shared mutation cache
  // (same fixedCacheKey the InvestigationPanel fires), so there's no 2nd request.
  const investigating = useAppSelector((s) => s.investigation.active);
  const [, { data: investigation }] = useInvestigateMutation({ fixedCacheKey: 'investigation' });

  // Re-cluster on map load + after each settled move (projection depends on
  // zoom/center, which aren't React state).
  const [clusterVersion, setClusterVersion] = useState(0);
  const bumpClusters = useCallback(() => setClusterVersion((v) => v + 1), []);
  const items = useClusters(alerts, mapRef, clusterVersion);

  // The feed flash is transient even though the popup stays open.
  useEffect(() => {
    if (!highlightedAlertId) return;
    const t = setTimeout(() => dispatch(clearHighlight()), 2000);
    return () => clearTimeout(t);
  }, [highlightedAlertId, dispatch]);

  // A selected single alert that's been purged (rule toggled off / aged out)
  // can no longer be shown — drop the selection so no orphan popup lingers.
  useEffect(() => {
    if (selection?.kind === 'alert' && !selectedAlert) dispatch(clearSelection());
  }, [selection, selectedAlert, dispatch]);

  // Close the open popup when the operator clicks anywhere outside it — the map
  // background, another part of the chrome (sidebar / live feed), etc. Clicks on
  // the popup itself and on pins are excluded (a pin click switches selection
  // via its own handler). Drag-safe: a pan moves the pointer, so a mousedown +
  // mousedown-up that travels is treated as a drag, not an outside click.
  useEffect(() => {
    if (!selection) return;
    let downX = 0;
    let downY = 0;
    const onDown = (e: MouseEvent) => {
      downX = e.clientX;
      downY = e.clientY;
    };
    const onUp = (e: MouseEvent) => {
      if (Math.abs(e.clientX - downX) > 4 || Math.abs(e.clientY - downY) > 4) return; // drag
      const t = e.target as HTMLElement | null;
      if (t?.closest('.maplibregl-popup') || t?.closest('.maplibregl-marker')) return;
      dispatch(clearSelection());
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('mouseup', onUp);
    };
  }, [selection, dispatch]);

  // Fit the map to the project's area whenever the selection changes.
  useEffect(() => {
    if (!project) return;
    const b = bboxOfPolygon(project.polygon);
    mapRef.current?.getMap().fitBounds(
      [
        [b.minLng, b.minLat],
        [b.maxLng, b.maxLat],
      ],
      { padding: 60, duration: 800 },
    );
  }, [project, mapRef]);

  // Centroid of the currently-selected cluster, for the cluster popup anchor.
  const selectedCluster =
    selection?.kind === 'cluster'
      ? items.find((it) => it.kind === 'cluster' && it.cluster.ruleId === selection.ruleId &&
          it.cluster.alerts.some((a) => selection.alertIds.includes(a.id)))
      : undefined;

  // A selected cluster whose members were all purged (rule stopped / aged out)
  // can no longer be anchored — drop the selection so no orphan handler lingers.
  useEffect(() => {
    if (selection?.kind === 'cluster' && !selectedCluster) dispatch(clearSelection());
  }, [selection, selectedCluster, dispatch]);

  // Decide which way each popup opens once, when it's first opened (the selected
  // alert/cluster changes), toward the side with screen room so a pin near the
  // top or an edge isn't clipped. Intentionally NOT recomputed on map move — the
  // popup stays anchored as the operator pans.
  const alertAnchor = useMemo(
    () =>
      selectedAlert
        ? choosePopupAnchor(mapRef.current?.getMap(), selectedAlert.lng, selectedAlert.lat, ALERT_POPUP_SIZE)
        : 'bottom',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAlert?.lng, selectedAlert?.lat, mapRef],
  );
  const clusterLngLat = selectedCluster?.kind === 'cluster' ? selectedCluster.cluster : undefined;
  const clusterAnchor = useMemo(
    () =>
      clusterLngLat
        ? choosePopupAnchor(mapRef.current?.getMap(), clusterLngLat.lng, clusterLngLat.lat, CLUSTER_POPUP_SIZE)
        : 'bottom',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clusterLngLat?.lng, clusterLngLat?.lat, mapRef],
  );

  // Stable selection handlers so the memoized marker layers below don't re-render
  // when an unrelated bit of map state (selection, highlight) changes.
  const onSelectAlert = useCallback((id: string) => dispatch(selectAlert(id)), [dispatch]);
  const onSelectCluster = useCallback(
    (ruleId: string, alertIds: string[]) => dispatch(selectCluster({ ruleId, alertIds })),
    [dispatch],
  );

  // The project polygon, the faint tower dots and the anomaly pins/clusters are
  // memoized into stable element trees. They depend ONLY on their own data, so
  // opening/closing a popup (a selection change) re-renders just the popup layer
  // below — React reuses these subtrees untouched.
  const projectLayer = useMemo(
    () =>
      project && (
        <Source id="project-polygon" type="geojson" data={project.polygon as unknown as GeoJSON.Geometry}>
          <Layer id="project-fill" type="fill" paint={{ 'fill-color': '#00e5ff', 'fill-opacity': 0.07 }} />
          <Layer
            id="project-outline"
            type="line"
            paint={{ 'line-color': '#00e5ff', 'line-width': 2, 'line-opacity': 0.6 }}
          />
        </Source>
      ),
    [project],
  );

  const towerMarkers = useMemo(
    () =>
      towers?.items.map((t) => (
        <Marker key={t.id} longitude={t.lng} latitude={t.lat}>
          <span
            style={{
              display: 'block',
              width: 4,
              height: 4,
              borderRadius: '50%',
              background: 'rgba(0,229,255,0.35)',
            }}
          />
        </Marker>
      )),
    [towers],
  );

  const alertMarkers = useMemo(
    () =>
      items.map((it) =>
        it.kind === 'pin' ? (
          <Marker key={it.alert.id} longitude={it.alert.lng} latitude={it.alert.lat}>
            <Pin
              severity={it.alert.severity}
              ruleId={it.alert.ruleId}
              delay={pinDelay(it.alert.id)}
              onSelect={() => onSelectAlert(it.alert.id)}
            />
          </Marker>
        ) : (
          <Marker key={it.cluster.id} longitude={it.cluster.lng} latitude={it.cluster.lat}>
            <ClusterPin
              cluster={it.cluster}
              onSelect={() => onSelectCluster(it.cluster.ruleId, it.cluster.alerts.map((a) => a.id))}
            />
          </Marker>
        ),
      ),
    [items, onSelectAlert, onSelectCluster],
  );

  return (
    <Map
      ref={mapRef}
      mapStyle={MAP_STYLE}
      initialViewState={{
        longitude: METRO_BBOX.centerLng,
        latitude: METRO_BBOX.centerLat,
        zoom: 12,
      }}
      style={{ position: 'absolute', inset: 0 }}
      onLoad={bumpClusters}
      onMoveEnd={bumpClusters}
    >
      {/* active project area */}
      {projectLayer}

      {/* Normal pins/towers hide while investigating (kept in Redux — not refetched). */}
      {!investigating && towerMarkers}
      {!investigating && alertMarkers}

      {/* Investigate Mode: the evidence layer for the drilled-into anomaly. */}
      {investigating && investigation && (
        <InvestigationLayer result={investigation} mapRef={mapRef} />
      )}

      {/* anchored detail popup — single alert */}
      {!investigating && selection?.kind === 'alert' && selectedAlert && (
        <Popup
          longitude={selectedAlert.lng}
          latitude={selectedAlert.lat}
          anchor={alertAnchor}
          offset={18}
          closeOnClick={false}
          onClose={() => dispatch(clearSelection())}
        >
          <AlertPopupBody alert={selectedAlert} />
        </Popup>
      )}

      {/* anchored detail popup — cluster */}
      {!investigating && selection?.kind === 'cluster' && selectedCluster?.kind === 'cluster' && (
        <Popup
          className="cluster-popup"
          longitude={selectedCluster.cluster.lng}
          latitude={selectedCluster.cluster.lat}
          anchor={clusterAnchor}
          offset={22}
          closeOnClick={false}
          onClose={() => dispatch(clearSelection())}
        >
          <ClusterPopupBody ruleId={selection.ruleId} alertIds={selection.alertIds} />
        </Popup>
      )}

      {/* Investigate Mode indicator — a fixed red frame + label over the map. */}
      {investigating && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            border: '2px solid #ff3b3b',
            boxShadow: 'inset 0 0 24px rgba(255,59,59,0.25)',
            pointerEvents: 'none',
            zIndex: 4,
          }}
        >
          <span
            className="eyebrow"
            style={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '3px 12px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1.5,
              color: '#fff',
              background: '#ff3b3b',
              borderRadius: 4,
              textTransform: 'uppercase',
            }}
          >
            Investigate Mode
          </span>
        </div>
      )}
    </Map>
  );
}
